import React from 'react';
import type { WorkflowEditorMetadataDraft } from 'src/modules/tools/types/workflow-editor.types';
import type { WorkflowDefinitionDraft } from 'src/modules/tools/utils/workflow-definition.type';

/** Use Workflow Editor Unsaved State Params contract. */
interface UseWorkflowEditorUnsavedStateParams {
  /** Current editable workflow definition. */
  definitionDraft?: WorkflowDefinitionDraft;
  /** Current editable workflow input schema. */
  inputSchemaDraft: Record<string, unknown>;
  /** Current editable workflow metadata. */
  metadataDraft: WorkflowEditorMetadataDraft;
  /** Updates the unsaved-change indicator. */
  setHasUnsavedChanges: (next: boolean) => void;
  /** Key identifying the current saved workflow baseline. */
  baselineKey?: string | null;
}

/** Use Workflow Editor Unsaved State Result contract. */
interface UseWorkflowEditorUnsavedStateResult {
  /** Replaces the baseline used to detect unsaved changes. */
  resetBaseline: () => void;
  /** Marks the current draft as the saved baseline. */
  markSaved: () => void;
}

export const useWorkflowEditorUnsavedState = ({
  definitionDraft,
  inputSchemaDraft,
  metadataDraft,
  setHasUnsavedChanges,
  baselineKey,
}: UseWorkflowEditorUnsavedStateParams): UseWorkflowEditorUnsavedStateResult => {
  const baselineSnapshotRef = React.useRef<string | null>(null);
  const lastBaselineKeyRef = React.useRef<string | null>(null);

  const currentSnapshot = React.useMemo(() => {
    if (!definitionDraft) return null;
    return JSON.stringify({
      definition: {
        ...definitionDraft,
        steps: definitionDraft.steps ?? {},
        inputSchema: inputSchemaDraft,
      },
      metadata: metadataDraft,
    });
  }, [definitionDraft, inputSchemaDraft, metadataDraft]);

  React.useEffect(() => {
    if (!currentSnapshot) return;
    const shouldTrackKey = baselineKey !== undefined;
    const keyChanged =
      shouldTrackKey && lastBaselineKeyRef.current !== baselineKey;
    if (keyChanged || baselineSnapshotRef.current === null) {
      baselineSnapshotRef.current = currentSnapshot;
      if (shouldTrackKey) {
        lastBaselineKeyRef.current = baselineKey ?? null;
      }
      setHasUnsavedChanges(false);
      return;
    }
    setHasUnsavedChanges(currentSnapshot !== baselineSnapshotRef.current);
  }, [baselineKey, currentSnapshot, setHasUnsavedChanges]);

  const resetBaseline = React.useCallback(() => {
    baselineSnapshotRef.current = null;
    setHasUnsavedChanges(false);
  }, [setHasUnsavedChanges]);

  const markSaved = React.useCallback(() => {
    if (currentSnapshot) {
      baselineSnapshotRef.current = currentSnapshot;
    }
    setHasUnsavedChanges(false);
  }, [currentSnapshot, setHasUnsavedChanges]);

  return { resetBaseline, markSaved };
};
